import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeftRight, LifeBuoy, LogOut, UserCog } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Field, NativeSelect } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const go = (p) => p.then(() => { window.location.href = "/app"; }).catch((e) => toast.error(errMsg(e)));

export function AccountSwitch({ dark }) {
  const { user } = useApp();
  if (user.support_session || !user.linked?.length) return null;
  return user.linked.map((l) => (
    <button key={l.id} data-testid={`switch-account-${l.is_hq ? "hq" : "clinic"}`} onClick={() => go(api.post("/auth/switch", { user_id: l.id }))}
      title={l.is_hq ? "Switch to Bloomwell" : `Switch to ${l.clinic_name}`}
      className={`h-10 shrink-0 inline-flex items-center gap-1.5 rounded-md border px-3 text-xs font-medium ${dark ? "border-white/20 hover:bg-white/10" : "hover:bg-black/5"}`}>
      <ArrowLeftRight className="h-3.5 w-3.5" /><span className="hidden 2xl:inline">{l.is_hq ? (l.is_return ? "Back to Bloomwell" : "Switch to Bloomwell") : `Switch to ${l.clinic_name}`}</span>
    </button>
  ));
}

export function MyAccount({ dark }) {
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", new_password: "", current_password: "" });
  if (user.support_session || user.via) return null;
  const save = () => api.put("/auth/account", { current_password: f.current_password, email: f.email || null, new_password: f.new_password || null })
    .then((r) => { toast.success(`Saved. Sign in with ${r.data.email} from now on.`); setOpen(false); setF({ email: "", new_password: "", current_password: "" }); })
    .catch((e) => toast.error(errMsg(e)));
  return (
    <>
      <button data-testid="my-account-btn" onClick={() => setOpen(true)} title="My account"
        className={`h-10 shrink-0 inline-flex items-center gap-1.5 rounded-md border px-3 text-xs font-medium ${dark ? "border-white/20 hover:bg-white/10" : "hover:bg-black/5"}`}><UserCog className="h-3.5 w-3.5" /><span className="hidden 2xl:inline">My account</span></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-background" data-testid="my-account-dialog">
          <DialogHeader><DialogTitle>My account</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Signed in as <b>{user.email}</b>. Change your login email or password here.</p>
          <Field label="New email (optional)"><Input data-testid="account-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="New password (optional, min 8)"><Input data-testid="account-new-password" type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} /></Field>
          <Field label="Current password"><Input data-testid="account-current-password" type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} /></Field>
          <Button className="rounded-full" data-testid="account-save" disabled={!f.current_password || (!f.email && !f.new_password)} onClick={save}>Save</Button>
          <div className="border-t pt-4"><AlertPrefs path="/auth/alerts" /></div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AlertPrefs({ path }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get(path).then((r) => setA(r.data)).catch(() => {}); }, [path]);
  if (!a) return null;
  const save = () => api.put(path, { ...a, alert_email: a.alert_email || null }).then((r) => { setA(r.data); toast.success("Booking alerts saved"); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-3" data-testid="alert-prefs">
      <p className="text-sm font-semibold">Booking alerts</p>
      <p className="text-xs text-muted-foreground">Sent in the clinic's name when a booking with this practitioner is made, moved or cancelled (not for bookings they make themselves).</p>
      <label className="flex items-center gap-2 text-sm"><Switch data-testid="alert-email-on" checked={a.alert_email_on} onCheckedChange={(v) => setA({ ...a, alert_email_on: v })} />Email</label>
      <Field label="Alert email (empty = login email)"><Input data-testid="alert-email" type="email" value={a.alert_email} onChange={(e) => setA({ ...a, alert_email: e.target.value })} /></Field>
      <label className="flex items-center gap-2 text-sm"><Switch data-testid="alert-whatsapp-on" checked={a.alert_whatsapp_on} onCheckedChange={(v) => setA({ ...a, alert_whatsapp_on: v })} />WhatsApp (needs the clinic's WhatsApp connected)</label>
      <Field label="Mobile for WhatsApp alerts"><Input data-testid="alert-phone" placeholder="+44 7700 900000" value={a.alert_phone} onChange={(e) => setA({ ...a, alert_phone: e.target.value })} /></Field>
      <Button size="sm" variant="outline" className="rounded-full" data-testid="alert-save" onClick={save}>Save alerts</Button>
    </div>
  );
}

export function SupportBanner() {
  const { user } = useApp();
  if (!user.support_session) return null;
  return (
    <div data-testid="support-banner" className="relative z-30 flex flex-wrap items-center justify-center gap-3 bg-amber-400 px-4 py-2 text-sm font-medium text-black">
      <LifeBuoy className="h-4 w-4" />Bloomwell support session in {user.clinic.name}: everything you do is recorded in the clinic's activity log.
      <Button size="sm" variant="outline" className="h-7 rounded-full bg-white/70" data-testid="exit-support-btn" onClick={() => go(api.post("/auth/exit-support"))}><LogOut className="h-3.5 w-3.5 mr-1" />Exit</Button>
    </div>
  );
}

export function enterClinic(id) {
  return go(api.post(`/platform/clinics/${id}/support-session`));
}

export function SupportAccess() {
  const { user } = useApp();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/clinic/support-access").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s || user.role !== "owner" || user.support_session) return null;
  const set = (hours) => api.put("/clinic/support-access", { hours }).then((r) => { setS(r.data); toast.success(hours ? "Bloomwell support can now help you" : "Support access removed"); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="rounded-[var(--radius)] border bg-card p-5 mb-6 space-y-3" data-testid="support-access">
      <p className="font-semibold flex items-center gap-2"><LifeBuoy className="h-4 w-4" />Bloomwell support access</p>
      <p className="text-sm text-muted-foreground">Let the Bloomwell team sign in to your clinic to help you. They get the same access as you, every action is recorded below, and access ends automatically.</p>
      <p className="text-sm" data-testid="support-access-status">{s.open ? `Allowed until ${new Date(s.until).toLocaleString()}` : "Not allowed"}</p>
      <div className="flex flex-wrap gap-2">
        {[[24, "Allow for 24 hours"], [168, "Allow for 7 days"]].map(([h, l]) => <Button key={h} size="sm" variant="outline" className="rounded-full" data-testid={`support-allow-${h}`} onClick={() => set(h)}>{l}</Button>)}
        {s.open && <Button size="sm" variant="ghost" className="text-destructive" data-testid="support-revoke" onClick={() => set(0)}>Remove access now</Button>}
      </div>
    </div>
  );
}

export function TeamPanel() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", email: "", platform_role: "support" });
  const load = () => api.get("/platform/team").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = () => api.post("/platform/team", f).then(() => { toast.success("Added. They set their password with \"Forgot password?\" on the login page."); setF({ ...f, name: "", email: "" }); load(); }).catch((e) => toast.error(errMsg(e)));
  const patch = (id, body) => api.patch(`/platform/team/${id}`, body).then(load).catch((e) => toast.error(errMsg(e)));
  const roles = [{ value: "support", label: "Support" }, { value: "sales", label: "Sales" }];
  return (
    <div className="rounded-[var(--radius)] border bg-card p-6 space-y-4" data-testid="platform-team">
      <h2 className="text-lg font-semibold">Bloomwell team</h2>
      <p className="text-sm text-muted-foreground">Support can manage clinics and help inside a clinic when its owner allows it. Sales can see new-clinic requests and create clinics. Neither can see any clinic's patients without permission.</p>
      <div className="divide-y">{list.map((m) => (
        <div key={m.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`team-${m.email}`}>
          <span className="font-medium flex-1 min-w-[160px]">{m.name} <span className="text-muted-foreground">· {m.email}</span></span>
          {m.platform_role === "owner" ? <span className="text-xs">Owner</span> : <>
            <NativeSelect testId={`team-role-${m.email}`} className="h-8 text-xs" value={m.platform_role} onChange={(v) => patch(m.id, { platform_role: v })} options={roles} />
            <Button size="sm" variant="ghost" data-testid={`team-toggle-${m.email}`} onClick={() => patch(m.id, { active: !m.active })}>{m.active ? "Deactivate" : "Reactivate"}</Button></>}
        </div>))}</div>
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <Field label="Name"><Input data-testid="team-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Email"><Input data-testid="team-email-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Role"><NativeSelect testId="team-role-select" value={f.platform_role} onChange={(v) => setF({ ...f, platform_role: v })} options={roles} /></Field>
        <Button className="rounded-full" data-testid="team-add-btn" disabled={!f.name || !f.email} onClick={add}>Add team member</Button>
      </div>
    </div>
  );
}
